import React, { useEffect, useRef, useState } from "react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";

import SecondaryPillTabs from "../../../../../components/tabs/SecondaryPillTabs";
import EmptyState from "../../../../../components/feedback/EmptyState";
import { EllipsisMenu } from "../../../../../components/menu/ellipsis/EllipsisMenu";
import FormModal from "../../../../../components/modals/FormModal";

import GroupMemberVideosView from "../../../../videos/groups/members/views/GroupMemberVideosView";
import GroupMemberAlbumsView from "../../../../albums/groups/members/views/GroupMemberAlbumsView";
import GroupMemberPopsView from "../../../../pops/groups/members/views/GroupMemberPopsView";
import GroupMemberStreamView from "../../../../streams/groups/members/views/GroupMemberStreamView";

import GroupCreatorEditForm from "../../forms/GroupCreatorEditForm";
import GroupCreatorMembers from "../groupMembers/GroupCreatorMembers";

import { useCreatorGroups } from "../../hooks/useCreatorGroups";

import "./GroupCreatorDashboard.css";

const PREVIEW_LIMIT = 5;
const PAGE_LIMIT = 12;

const GROUP_TABS = [
  { id: "home", label: "Home" },
  { id: "videos", label: "Videos" },
  { id: "albums", label: "Albums" },
  {
    id: "more",
    label: "More",
    menu: [
      { id: "pops", label: "Pops" },
      { id: "streams", label: "Streams" },
      { id: "members", label: "Members" },
    ],
  },
];

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unable to load group.";

export default function GroupCreatorDashboard({
  username,
  groupTag,
  onDeleteGroup,
}) {
  const tag = String(groupTag || "").trim();

  const { getCreatorGroup } = useCreatorGroups(username);

  const getGroupRef = useRef(getCreatorGroup);
  const requestRef = useRef(0);

  const [groupState, setGroupState] = useState({
    username: "",
    tag: "",
    data: null,
    loading: true,
    error: "",
  });

  const [activeTab, setActiveTab] = useState("home");
  const [editingGroup, setEditingGroup] = useState(null);

  getGroupRef.current = getCreatorGroup;

  const isCurrent = groupState.username === username && groupState.tag === tag;

  const group = isCurrent ? groupState.data : null;
  const loading = !isCurrent || groupState.loading;
  const error = isCurrent ? groupState.error : "";
  const ready = Boolean(group) && !loading;

  const isOwner = group?.isOwner === true || Number(group?.isOwner) === 1;

  const tabs = isOwner
    ? GROUP_TABS
    : GROUP_TABS.map((tab) =>
        tab.id === "more"
          ? {
              ...tab,
              menu: tab.menu.filter((item) => item.id !== "members"),
            }
          : tab,
      );

  useEffect(() => {
    const requestId = ++requestRef.current;

    setGroupState({
      username,
      tag,
      data: null,
      loading: true,
      error: "",
    });

    setActiveTab("home");
    setEditingGroup(null);

    if (!username || !tag) {
      setGroupState({
        username,
        tag,
        data: null,
        loading: false,
        error: "Creator username and group tag are required.",
      });

      return () => {
        requestRef.current += 1;
      };
    }

    const loadGroup = async () => {
      try {
        const data = await getGroupRef.current(tag);

        if (requestId !== requestRef.current) return;

        if (String(data?.groupTag || "") !== tag) {
          throw new Error("Group details do not match.");
        }

        setGroupState({
          username,
          tag,
          data,
          loading: false,
          error: "",
        });
      } catch (requestError) {
        if (requestId !== requestRef.current) return;

        setGroupState({
          username,
          tag,
          data: null,
          loading: false,
          error: getErrorMessage(requestError),
        });
      }
    };

    void loadGroup();

    return () => {
      requestRef.current += 1;
    };
  }, [username, tag]);

  const handleTabChange = (value) => {
    const next =
      typeof value === "string"
        ? value
        : value?.id || value?.value || value?.path;

    const validTabs = [
      "home",
      "videos",
      "albums",
      "pops",
      "streams",
      "members",
    ];

    if (!validTabs.includes(next) || next === activeTab) {
      return;
    }

    if (next === "members" && !isOwner) {
      return;
    }

    setActiveTab(next);
  };

  const handleEditSuccess = (result) => {
    if (!result?.group) return;

    setGroupState((current) => {
      if (
        current.username !== username ||
        current.tag !== tag ||
        !current.data
      ) {
        return current;
      }

      return {
        ...current,
        data: {
          ...current.data,
          ...result.group,
        },
      };
    });

    setEditingGroup(null);
  };

  const renderHome = () => (
    <>
      <GroupMemberVideosView groupTag={tag} limit={PREVIEW_LIMIT} preview />

      <GroupMemberAlbumsView groupTag={tag} limit={PREVIEW_LIMIT} preview />

      <GroupMemberPopsView groupTag={tag} limit={PREVIEW_LIMIT} preview />

      <GroupMemberStreamView groupTag={tag} limit={PREVIEW_LIMIT} preview />
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "videos":
        return <GroupMemberVideosView groupTag={tag} limit={PAGE_LIMIT} />;

      case "albums":
        return <GroupMemberAlbumsView groupTag={tag} limit={PAGE_LIMIT} />;

      case "pops":
        return <GroupMemberPopsView groupTag={tag} limit={PAGE_LIMIT} />;

      case "streams":
        return <GroupMemberStreamView groupTag={tag} limit={PAGE_LIMIT} />;

      case "members":
        return isOwner ? (
          <GroupCreatorMembers username={username} groupTag={tag} />
        ) : null;

      default:
        return renderHome();
    }
  };

  return (
    <ViewContainer>
      {loading && (
        <p className="creator-groups-dashboard__loading" role="status">
          Loading group...
        </p>
      )}

      {!loading && error && (
        <ContentPanel>
          <EmptyState title="Unable to load group" description={error} />
        </ContentPanel>
      )}

      {ready && (
        <>
          <ContentPanel>
            <ContentPanelHeader
              title={group.groupName || "Group"}
              subTitle={group.groupCode || "Creator group"}
            >
              {isOwner && (
                <EllipsisMenu
                  position="right"
                  options={[
                    {
                      label: "Edit Group",
                      action: () => setEditingGroup(group),
                    },
                    {
                      label: "Delete Group",
                      action: () => onDeleteGroup?.(group),
                      variant: "danger",
                      disabled: !onDeleteGroup,
                    },
                  ]}
                />
              )}
            </ContentPanelHeader>

            <SecondaryPillTabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={handleTabChange}
            />
          </ContentPanel>

          <div className="creator-groups-dashboard__content">
            {renderContent()}
          </div>
        </>
      )}

      <FormModal
        isOpen={Boolean(editingGroup)}
        title="Edit Group"
        subTitle="Update your group details."
        onClose={() => setEditingGroup(null)}
        maxWidth={540}
      >
        {editingGroup && (
          <GroupCreatorEditForm
            key={editingGroup.groupTag}
            username={username}
            group={editingGroup}
            onClose={() => setEditingGroup(null)}
            onSuccess={handleEditSuccess}
          />
        )}
      </FormModal>
    </ViewContainer>
  );
}
